import type { ComponentProps, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { tv, type VariantProps } from "tailwind-variants";

const statusCardVariants = tv({
	base: "rounded-xl border border-border-primary bg-white p-5 text-left shadow-xs",
	variants: {
		variant: {
			default: "",
			info: "",
			success: "",
			warning: "",
			error: "",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});

const statusCardIconContainerVariants = tv({
	base: "mb-5 flex h-12 w-12 items-center justify-center rounded-full p-3",
	variants: {
		variant: {
			default: "bg-[#F5F5F5]",
			info: "bg-blue-100",
			success: "bg-green-100",
			warning: "bg-orange-100",
			error: "bg-red-100",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});

const statusCardIconVariants = tv({
	base: "h-5 w-5",
	variants: {
		variant: {
			default: "text-utility-gray-500",
			info: "text-blue-500",
			success: "text-green-500",
			warning: "text-orange-500",
			error: "text-red-500",
		},
	},
	defaultVariants: {
		variant: "default",
	},
});

export type StatusCardProps = ComponentProps<"div"> &
	VariantProps<typeof statusCardVariants> & {
		icon: ReactNode;
		title: string;
		subtitle?: string;
		value: number | string;
	};

export const StatusCard = ({
	icon,
	title,
	subtitle,
	value,
	variant,
	className,
	...props
}: StatusCardProps) => (
	<div
		className={twMerge(statusCardVariants({ variant }), className)}
		data-slot="status-card"
		{...props}
	>
		<div className={statusCardIconContainerVariants({ variant })}>
			<div className={statusCardIconVariants({ variant })}>{icon}</div>
		</div>
		<div className="mb-2 space-y-1">
			<p className="mb-0 font-semibold text-primary text-sm">{title}</p>
			{subtitle ? (
				<p className="mb-0 font-medium text-text-tertiary text-xs">
					{subtitle}
				</p>
			) : null}
		</div>
		<p className="font-semibold text-[32px] text-primary leading-10">{value}</p>
	</div>
);
